import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Detail } from "./Detail";
import { SIGNAL } from "../Sticker";

const MONO = "JetBrains Mono, monospace";
const INK = "#0D1B2A";

const STAGES = [
  { n: "01", title: "Fresh", fill: SIGNAL.green, text: "Low concentration of volatile bases. The dye keeps its original purple." },
  { n: "02", title: "Early spoilage", fill: SIGNAL.yellow, text: "Spoilage compounds begin to accumulate in the headspace. The purple starts to shift towards yellow as the local pH rises." },
  { n: "03", title: "Spoiled", fill: SIGNAL.red, text: "The dye reaches its final yellow. The change is visible through the viewing window on the package." },
];

const READING = ["PURPLE — CONSUME", "PURPLE-YELLOW — TODAY", "YELLOW — DISCARD"];

export const ColourDiagram = () => {
  const [sel, setSel] = useState(0);
  const [narrow, setNarrow] = useState(false);
  const s = STAGES[sel];

  useEffect(() => {
    setNarrow(window.matchMedia("(max-width: 767px)").matches);
  }, []);

  return (
    <div className="grid grid-cols-12 gap-x-6 gap-y-8" data-testid="colour-diagram">
      <div className="col-span-12 lg:col-span-7">
        {narrow ? (
          <svg viewBox="0 0 360 420" className="w-full h-auto">
            <line x1="62" y1="30" x2="62" y2="368" stroke={INK} strokeOpacity="0.2" />
            {STAGES.map((st, i) => {
              const cy = 60 + i * 130;
              const on = i === sel;
              return (
                <g key={st.n} onClick={() => setSel(i)} data-testid={`colour-stage-${st.n}`} role="button" aria-pressed={on}>
                  <rect x="0" y={cy - 56} width="360" height="112" fill="transparent" />
                  <motion.circle cx="62" cy={cy} r="42" fill={st.fill} stroke={INK} animate={{ strokeWidth: on ? 2 : 1, opacity: on ? 1 : 0.55 }} />
                  <text x="128" y={cy - 4} fontFamily={MONO} fontSize="13" letterSpacing="1.5" fill={INK} opacity={on ? 1 : 0.55}>{st.title.toUpperCase()}</text>
                  <text x="128" y={cy + 18} fontFamily={MONO} fontSize="10" letterSpacing="0.5" fill="#595959" opacity={on ? 1 : 0.55}>{READING[i]}</text>
                  {i < 2 && <path d={`M62 ${cy + 60} L 62 ${cy + 80} M56 ${cy + 72} L 62 ${cy + 80} L 68 ${cy + 72}`} fill="none" stroke={INK} strokeOpacity="0.45" />}
                </g>
              );
            })}
            <motion.circle cx="14" cy={60} r="6" fill={INK} initial={{ cy: 60 + sel * 130 }} animate={{ cy: 60 + sel * 130 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} data-testid="colour-marker" />
            <text x="14" y="404" fontFamily={MONO} fontSize="10" letterSpacing="0.5" fill="#595959">SPOILAGE COMPOUNDS INCREASE ↓</text>
          </svg>
        ) : (
          <svg viewBox="0 0 640 260" className="w-full h-auto">
            {STAGES.map((st, i) => {
              const cx = 110 + i * 210;
              const on = i === sel;
              return (
                <g key={st.n} onClick={() => setSel(i)} style={{ cursor: "pointer" }} data-testid={`colour-stage-${st.n}`} role="button" aria-pressed={on}>
                  <rect x={cx - 80} y="20" width="160" height="190" fill="transparent" />
                  <motion.circle cx={cx} cy="100" r="64" fill={st.fill} stroke={INK} animate={{ strokeWidth: on ? 2 : 1, opacity: on ? 1 : 0.6 }} />
                  <text x={cx} y="192" textAnchor="middle" fontFamily={MONO} fontSize="11" letterSpacing="1.5" fill={INK}>{st.title.toUpperCase()}</text>
                  {i < 2 && <path d={`M${cx + 82} 100 L ${cx + 122} 100 M${cx + 114} 94 L ${cx + 122} 100 L ${cx + 114} 106`} fill="none" stroke={INK} />}
                </g>
              );
            })}
            <line x1="46" y1="228" x2="594" y2="228" stroke={INK} strokeOpacity="0.25" />
            <motion.circle cy="228" r="5" fill={INK} animate={{ cx: 110 + sel * 210 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} data-testid="colour-marker" />
            <text x="46" y="252" fontFamily={MONO} fontSize="9" letterSpacing="1" fill="#595959">SPOILAGE COMPOUNDS INCREASE →</text>
          </svg>
        )}
      </div>
      <div className="col-span-12 lg:col-span-5">
        <Detail n={s.n} title={s.title} text={s.text} testId="colour-detail" />
        <p className="mono text-[0.65rem] tracking-[0.15em] uppercase text-[#595959] mt-6">
          The dye response is read through the window: purple fresh, purple-yellow use today, yellow expired
        </p>
      </div>
    </div>
  );
};
